import type { Theme } from '@mui/material/styles';
import type { ChartMode } from 'src/theme/chart-palette';

import { merge } from 'es-toolkit';
import { varAlpha } from 'minimal-shared/utils';

import { useTheme, useColorScheme } from '@mui/material/styles';

import { fNumber } from 'src/utils/format/format-number';
import { formatCompactNumber } from 'src/utils/format/format-compact-number';

import { CHART_SERIES } from 'src/theme/chart-palette';


// ----------------------------------------------------------------------

// "예쁜" 숫자로 반올림하는 함수
const getNiceScale = (max: number) => {
  if (max <= 0) return 1;
  const exp = Math.floor(Math.log10(max));
  const frac = max / Math.pow(10, exp);
  let nice;
  if (frac <= 1) nice = 1;
  else if (frac <= 2) nice = 2;
  else if (frac <= 5) nice = 5;
  else nice = 10;
  return nice * Math.pow(10, exp);
};

// Y축 데이터에서 최대값을 계산하고 nice scale을 적용하는 함수
const getYAxisWithNiceScale = (yaxisConfig: any, seriesData: any[]): any[] => {
  if (!Array.isArray(yaxisConfig)) {
    yaxisConfig = [yaxisConfig];
  }

  const shared = yaxisConfig.length === 1;

  return yaxisConfig.map((axis: any, index: number) => {
    // 해당 시리즈의 데이터 찾기. 축이 하나뿐이면 모든 시리즈가 그 축을 쓰므로
    // 최댓값도 전체 시리즈에서 구한다 — 첫 시리즈만 보면 다른 시리즈(예: 직전 창)가 위로 잘린다.
    const seriesIndex = axis.seriesName ?
      seriesData.findIndex((s: any) => s.name === axis.seriesName) :
      index;
    const values: number[] = shared && !axis.seriesName
      ? seriesData.flatMap((s: any) => (Array.isArray(s?.data) ? s.data : []))
      : seriesIndex >= 0 && seriesData[seriesIndex]?.data ? seriesData[seriesIndex].data : [];

    if (values.length > 0) {
      const maxValue = Math.max(...values.map((v: any) => Number(v) || 0), 1);
      const niceMax = getNiceScale(maxValue);

      return {
        ...axis,
        min: axis.min ?? 0,
        max: axis.max ?? niceMax,
        forceNiceScale: axis.forceNiceScale ?? true,
      };
    }

    return axis;
  });
};

/**
 * 화면 옵션을 **공용 차트 테마** 위에 얹는다 — 색·격자·축 글자·툴팁·막대·도넛의 모양은 여기 한 곳이 정한다.
 *
 * 🔑 화면은 «뜻»(계열·축·포맷)만 준다 — 모양을 화면마다 다시 적지 마라.
 * ⚠️ 배열은 **칸끼리** 합쳐진다(`colors: ['#a']` → 기본 2번 칸부터는 그대로) · 객체 자리에 배열을 주면 통째로 바뀐다.
 * ⚠️ 화면이 y축 눈금 표기도 툴팁 표기도 안 주면 툴팁은 **전체 수**(`fNumber`)로 적는다 —
 *   안 그러면 상류가 축의 짧은 표기(`1.2K`)를 툴팁에 그대로 쓴다.
 */
export function useChart(updatedOptions?: any, seriesData: any[] = []) {
  const theme = useTheme();
  const baseOptions = baseChartOptions(theme, useChartMode() === 'dark') ?? {};

  // Y축에 nice scale 적용 (더 안전하게)
  if (updatedOptions?.yaxis &&
    Array.isArray(seriesData) &&
    seriesData.length > 0 &&
    seriesData.every((s: any) => s && s.data && Array.isArray(s.data) && s.data.length > 0)) {
    try {
      updatedOptions.yaxis = getYAxisWithNiceScale(updatedOptions.yaxis, seriesData);
    } catch (error) {
      console.warn('useChart: Y축 nice scale 적용 중 오류 발생:', error);
    }
  }

  const merged = merge(baseOptions, updatedOptions ?? {});
  if (!hasYFormatter(updatedOptions?.yaxis) && !updatedOptions?.tooltip?.y?.formatter) {
    merged.tooltip = { ...merged.tooltip, y: { ...merged.tooltip?.y, formatter: tooltipValue } };
  }
  return merged;
}

/** 축 눈금 — 수면 짧게(`1.2K`), 아니면(가로 막대의 이름 축) 그대로 */
function axisValue(value: unknown): unknown {
  if (typeof value !== 'number') return value;
  return formatCompactNumber(value, { nonPositive: 'keep', trimPointZero: true, below: 'fNumber' });
}

/** 툴팁 값 — 수면 전체 수, 아니면 그대로 */
function tooltipValue(value: unknown): unknown {
  return typeof value === 'number' && Number.isFinite(value) ? fNumber(value) : value;
}

function hasYFormatter(yaxis: any): boolean {
  if (Array.isArray(yaxis)) return yaxis.some((axis) => Boolean(axis?.labels?.formatter));
  return Boolean(yaxis?.labels?.formatter);
}

// ----------------------------------------------------------------------


/**
 * 공용 차트 테마 — dataviz 규격(선 2px · 마커는 hover 때만 ≥8px · 격자는 수평 실선 한 가닥 ·
 * 축 선 없음 · 막대 끝 4px · 면은 옅은 그라데이션 · 도넛 가운데 합계).
 * 조각·막대 사이 2px 틈과 툴팁 겉모양은 `styles.css` 가 쥔다(CSS 라 다크 모드를 따라간다).
 */
const baseChartOptions = (theme: Theme, dark: boolean) => {
  const axisText = { colors: theme.vars.palette.text.secondary, fontSize: '12px' };

  const LABEL_TOTAL = {
    show: true,
    label: '합계',
    color: theme.vars.palette.text.secondary,
    fontSize: theme.typography.subtitle2.fontSize,
    fontWeight: theme.typography.subtitle2.fontWeight,
  };

  const LABEL_VALUE = {
    offsetY: 8,
    color: theme.vars.palette.text.primary,
    fontSize: theme.typography.h4.fontSize,
    fontWeight: 600,
  };

  return {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
      parentHeightOffset: 0,
      fontFamily: theme.typography.fontFamily,
      foreColor: theme.vars.palette.text.secondary,
      animations: {
        enabled: true,
        speed: 360,
        animateGradually: { enabled: true, delay: 120 },
        dynamicAnimation: { enabled: true, speed: 360 },
      },
    },

    /* 범주 색 — 정해진 차례(`src/theme/chart-palette.ts`) · 상태색은 쓰지 않는다 */
    colors: [...CHART_SERIES[dark ? 'dark' : 'light']],

    states: {
      hover: { filter: { type: 'lighten' } },
      active: { filter: { type: 'darken' } },
    },

    fill: {
      opacity: 1,
      gradient: {
        type: 'vertical',
        shadeIntensity: 0,
        opacityFrom: 0.18,
        opacityTo: 0.02,
        stops: [0, 100],
      },
    },

    dataLabels: { enabled: false },

    /* ⚠️ `monotoneCubic` — 부드럽되 점 사이에서 값보다 위·아래로 튀지 않는다(`smooth` 는 0 아래로 내려간다) */
    stroke: { width: 2, curve: 'monotoneCubic', lineCap: 'round' },

    grid: {
      strokeDashArray: 0,
      borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
      padding: { top: 0, right: 8, bottom: 0 },
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },

    xaxis: {
      axisBorder: { show: false },
      axisTicks: { show: false },
      /* 날짜 칸이 빽빽하면 눕히지 않고 겹치는 것만 걷는다 */
      labels: { style: axisText, rotate: 0, hideOverlappingLabels: true },
      crosshairs: {
        show: true,
        width: 1,
        stroke: { color: theme.vars.palette.text.disabled, width: 1, dashArray: 3 },
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      tickAmount: 5,
      labels: { style: axisText, formatter: axisValue },
    },

    /* 점은 평소엔 숨기고, 가리킬 때만 10px 로 띄운다(바탕색 고리 2px) */
    markers: {
      size: 0,
      strokeWidth: 2,
      strokeColors: theme.vars.palette.background.paper,
      hover: { size: 5 },
    },

    tooltip: { theme: 'false', fillSeriesColor: false, x: { show: true } },

    /* 계열이 둘 이상이면 위 왼쪽에 작은 점 + 이름 · 하나면 상류가 알아서 숨긴다(`showForSingleSeries`) */
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontWeight: 500,
      fontSize: '12px',
      markers: { shape: 'circle', size: 5, strokeWidth: 0 },
      labels: { colors: theme.vars.palette.text.secondary },
      itemMargin: { horizontal: 8, vertical: 4 },
    },

    plotOptions: {
      /* 막대는 가늘게, 끝만 4px 둥글게(바닥 쪽은 각) */
      bar: {
        borderRadius: 4,
        borderRadiusApplication: 'end',
        borderRadiusWhenStacked: 'last',
        columnWidth: '56%',
        barHeight: '64%',
      },
      pie: {
        expandOnClick: false,
        donut: {
          size: '72%',
          labels: { show: true, value: { ...LABEL_VALUE }, total: { ...LABEL_TOTAL } },
        },
      },
      radialBar: {
        hollow: { margin: -8, size: '100%' },
        track: {
          margin: -8,
          strokeWidth: '50%',
          background: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
        },
        dataLabels: { value: { ...LABEL_VALUE }, total: { ...LABEL_TOTAL } },
      },
      radar: {
        polygons: {
          fill: { colors: ['transparent'] },
          strokeColors: theme.vars.palette.divider,
          connectorColors: theme.vars.palette.divider,
        },
      },
      polarArea: {
        rings: { strokeColor: theme.vars.palette.divider },
        spokes: { connectorColors: theme.vars.palette.divider },
      },
      heatmap: { distributed: true },
    },

    responsive: [
      {
        breakpoint: theme.breakpoints.values.sm, // sm ~ 600
        options: { plotOptions: { bar: { borderRadius: 4, columnWidth: '72%' } } },
      },
    ],
  };
};

/**
 * 지금 그리는 색 모드 — 계열 색을 화면이 직접 골라야 할 때(`chartSeriesColor` · `chartChannelColor`) 쓴다.
 * @returns `'dark'` 는 사용자가 다크를 골랐거나 시스템 다크를 따를 때뿐이다
 */
export function useChartMode(): ChartMode {
  const { mode, systemMode } = useColorScheme();
  return (mode === 'system' ? systemMode : mode) === 'dark' ? 'dark' : 'light';
}
